import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Check, FileSearch, KeyRound, Lock, Plug, ScrollText, Users, X,
} from "lucide-react";
import { useBookAssessment } from "../hooks/useBookAssessment";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import { buildServiceSchema, type Crumb } from "../utils/structuredData";

// The catalog adds this offering to the visitor's plan and opens its page.
const ADD_TO_PLAN = "/catalog?add=virtual-archives";
const ONE_PAGER = "/assets/1-pagers/Virtual%20Archives%201-Pager.pdf";

const crumbs: Crumb[] = [
  { name: "Home", path: "/" },
  { name: "Virtual Archives", path: "/virtual-archives" },
];

const OVERVIEW =
  "Virtual Archives is the private context layer for your AI. It indexes your documents once, inside your own " +
  "secure environment, and hands your AI one cited package per question instead of making it read hundreds of " +
  "pages. Your team asks in plain language and gets an answer that links to the exact source page, drawn only " +
  "from files they are already allowed to open. Run it fully air-gapped on your own hardware with a small local " +
  "model, or connect Claude, Codex, Cursor or an internal agent through MCP while your files and keys stay on your " +
  "server.";

const BARRIERS = [
  { title: "Data can't leave", body: "Records sent to cloud AI leave your control and can be exposed in transit or processing.",
    verdict: "Off the table for air-gapped sites and confidential information." },
  { title: "Compliance is out of reach", body: "Most AI tools don't fit HIPAA, FERPA or records-retention policies. The ones that do need expensive enterprise plans or custom setups.",
    verdict: "Too risky or too expensive to adopt." },
  { title: "AI misses the context", body: "Thousands of documents, each with different permissions. AI pulls the wrong source, crosses permission lines or makes up answers.",
    verdict: "Close isn't good enough when the stakes are high." },
];

const WITHOUT = ["AI searches files one by one", "Reads hundreds of pages per question",
                 "Most of the compute goes to finding context", "Needs a big cloud model to get it right"];
const WITH = ["Documents indexed once, on your own hardware", "AI gets one cited package per question",
              "Compute goes to answers, not search", "Small local models answer like frontier models, or better"];

const PROOF = [
  { stat: "98%", label: "Improved accuracy", body: "A local model scored 98% with Virtual Archives, up from 83% without it." },
  { stat: "59/60", label: "Beats cloud AI", body: "A local model with Virtual Archives scored 59 of 60. Claude Haiku reading the files directly scored 57." },
  { stat: "0", label: "Invented citations", body: "Every answer links to its source page. None pointed at a page that isn't there." },
  { stat: "77%", label: "Less compute", body: "Token use fell 77% on questions answered from documents." },
  { stat: "10K", label: "Documents, one server", body: "At 10,000 documents accuracy held, while plain AI needed 2.8 times the tokens." },
  { stat: "40%", label: "Faster answers", body: "Average answer time dropped from 22 seconds to 13." },
];

const COMPLIANCE = [
  { icon: Lock, title: "Fully air-gapped", body: "The model, documents, index and encryption keys all stay on your hardware. Live at Newark City Hall." },
  { icon: Plug, title: "Or connected via MCP", body: "Connect Claude, Codex, Cursor or an internal agent through MCP. Your files and keys stay on your server." },
  { icon: Users, title: "Need-to-know access", body: "Staff only see answers from files they're already allowed to open. Zero leaks across 270 automated tests on SharePoint, Drive and Dropbox." },
  { icon: KeyRound, title: "Encrypted throughout", body: "Encrypted database and library, key rotation, malware scanning, and nightly encrypted backups with verified restores." },
  { icon: ScrollText, title: "Records and retention", body: "Retention classification built for HIPAA, FERPA and municipal records rules, with Microsoft Entra or Google sign-on." },
  { icon: FileSearch, title: "Audit-ready trails", body: "Every search, read and answer is logged and cited to its source page, and forwards to your SIEM for audits and records requests." },
];

const USE_CASES = [
  { title: "Local Government", description: "Search decades of permits, ordinances and public works records, with every answer cited for records requests." },
  { title: "Healthcare", description: "Find answers across policies and patient records without sending protected health information to the cloud." },
  { title: "Legal Professionals", description: "Cross-reference thousands of case files and contracts, with each answer pointing to the exact page." },
  { title: "Education", description: "Give staff answers from student and policy records while FERPA permissions stay intact." },
  { title: "Financial Auditing", description: "Query statements and reports privately to spot anomalies, with a full audit trail of every question." },
  { title: "Defense and Research", description: "Bring AI to air-gapped labs and secure sites where nothing can leave the building." },
];

const VirtualArchives: React.FC = () => {
  const bookAssessment = useBookAssessment();
  return (
    <div className="relative pt-6">
      <Seo
        title="Virtual Archives | Contextral"
        description="The private context layer for your AI: cited answers from your own documents, air-gapped on your hardware or connected through MCP."
        jsonLd={buildServiceSchema("/virtual-archives", "Virtual Archives", OVERVIEW)}
      />

      <section>
        <Breadcrumbs items={crumbs} />
        <div className="text-center mb-10 sm:mb-14">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-text-primary mb-4">Virtual Archives</h1>
          <p className="text-base sm:text-lg md:text-xl text-text-secondary max-w-4xl mx-auto leading-relaxed">
            The private context layer for your AI. Run AI agents faster, cheaper, and more accurate with frontier or private local models
          </p>
        </div>
      </section>

      {/* Overview, with the demo video under it */}
      <section className="flex flex-col gap-6">
        
        <div className="rounded-2xl border border-borderSubtle bg-surface p-6 sm:p-8 shadow-sm">
          <h2 className="text-2xl sm:text-3xl font-bold text-text-primary mb-4">Overview</h2>
          <p className="text-lg leading-relaxed text-text-secondary">{OVERVIEW}</p>
          <div className="mt-6">
            <a href={ONE_PAGER} download="Virtual-Archives-1-Pager.pdf"
               className="inline-flex items-center gap-3 px-5 py-3 bg-primary text-text-inverse font-semibold rounded-full shadow hover:bg-primaryHover transition">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v12m0 0l-4-4m4 4l4-4M4 20h16" />
              </svg>
              Download the 1-Pager
            </a>
          </div>
        </div>
        <div className="rounded-2xl border border-borderSubtle bg-surface shadow-sm">
          <video src="/assets/demos/virtualArchives.mp4" controls className="w-full rounded-2xl shadow-2xl"
                 aria-label="A demo video showing the Virtual Archives in action." />
        </div>
      </section>

      {/* The problem */}
      <section aria-labelledby="problem-title" className="relative max-w-full overflow-hidden bg-surfaceSoft py-12 sm:py-16 md:py-20 mb-0">
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <div className="absolute -top-24 -left-24 h-80 w-80 rounded-full bg-primary/10 blur-3xl" />
          <div className="absolute -bottom-24 -right-16 h-96 w-96 rounded-full bg-accentPurple/10 blur-3xl" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 id="problem-title" className="text-3xl sm:text-4xl font-bold text-text-primary mb-4">AI can't reach sensitive data</h2>
            <p className="max-w-3xl mx-auto text-text-secondary">
              The same wall exists everywhere sensitive records live: government, healthcare, legal, education,
              defense and finance. Three barriers keep them from adopting AI at all.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {BARRIERS.map((b) => (
              <div key={b.title} className="flex flex-col gap-3 rounded-2xl border border-borderSubtle bg-surface p-6 sm:p-8 shadow-sm">
                <h3 className="text-xl font-bold text-primary">{b.title}</h3>
                <p className="text-text-secondary">{b.body}</p>
                <p className="mt-auto pt-2 font-semibold italic text-text-primary">{b.verdict}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Without vs with */}
      <section aria-labelledby="compare-title" className="py-12 sm:py-16 md:py-20 mb-0">
        <div className="text-center mb-10">
          <h2 id="compare-title" className="text-3xl sm:text-4xl font-bold text-text-primary mb-4">Context, not answers, is the expensive part</h2>
          <p className="max-w-3xl mx-auto text-text-secondary">
            Building the context, not writing the answer, takes AI the most time and compute. Virtual Archives
            does that work once, so every question is quick.
          </p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="flex flex-col gap-4 rounded-2xl border border-borderSubtle bg-surface p-6 sm:p-8 shadow-sm">
            <h3 className="text-2xl font-bold text-text-primary">Without Virtual Archives</h3>
            <ul className="flex flex-col gap-3">
              {WITHOUT.map((line) => (
                <li key={line} className="flex items-start gap-3 text-text-secondary">
                  <X className="mt-1 h-5 w-5 flex-none text-text-muted" aria-hidden="true" />{line}
                </li>
              ))}
            </ul>
            <p className="mt-auto pt-2 font-semibold text-primary">Costly, slow, and misses or fakes sources.</p>
          </div>
          <div className="relative flex flex-col gap-4 overflow-hidden rounded-2xl bg-primary p-6 sm:p-8 shadow-xl shadow-primary/25">
            <div className="pointer-events-none absolute inset-0 bg-blueprint opacity-30" aria-hidden="true" />
            <h3 className="relative text-2xl font-bold text-text-inverse">With Virtual Archives</h3>
            <ul className="relative flex flex-col gap-3">
              {WITH.map((line) => (
                <li key={line} className="flex items-start gap-3 text-surfaceSoft">
                  <Check className="mt-1 h-5 w-5 flex-none text-white" aria-hidden="true" />{line}
                </li>
              ))}
            </ul>
            <p className="relative mt-auto pt-2 font-semibold text-white">Cheap, fast, and every answer has a source you can check.</p>
          </div>
        </div>
        <p className="mt-8 text-center text-lg font-semibold text-primary">
          Run your current model for far less, or let a smaller model in-house get it right.
        </p>
      </section>

      {/* Proof */}
      <section aria-labelledby="proof-title" className="relative max-w-full overflow-hidden bg-surfaceSoft py-12 sm:py-16 md:py-20 mb-0">
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <div className="absolute -top-24 -left-24 h-80 w-80 rounded-full bg-primary/10 blur-3xl" />
          <div className="absolute -bottom-24 -right-16 h-96 w-96 rounded-full bg-accentPurple/10 blur-3xl" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 id="proof-title" className="text-3xl sm:text-4xl font-bold text-text-primary mb-4">The proof</h2>
            <p className="max-w-2xl mx-auto text-text-secondary">Same open model, same GPU. Virtual Archives is the difference.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PROOF.map((p) => (
              <div key={p.label} className="flex flex-col gap-1.5 rounded-xl border border-borderSubtle bg-surface p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-accentPurple/10">
                <span className="font-display text-4xl font-bold text-primary">{p.stat}</span>
                <h3 className="text-lg font-bold text-text-primary">{p.label}</h3>
                <p className="text-text-secondary">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance */}
      <section aria-labelledby="compliance-title" className="py-12 sm:py-16 md:py-20 mb-0">
        <div className="text-center mb-10">
          <h2 id="compliance-title" className="text-3xl sm:text-4xl font-bold text-text-primary mb-4">Compliance built in</h2>
          <p className="max-w-2xl mx-auto text-text-secondary">
            The compliance of an air-gapped system with the ease of a cloud product.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {COMPLIANCE.map((c) => (
            <div key={c.title} className="rounded-2xl border border-borderSubtle bg-surface p-6 sm:p-8 shadow-sm">
              <c.icon className="mb-4 h-8 w-8 text-primary" aria-hidden="true" />
              <h3 className="text-xl font-bold text-text-primary mb-2">{c.title}</h3>
              <p className="text-text-secondary">{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Use cases */}
      <section className="py-12 sm:py-16 md:py-20 mb-0">
        <div className="container mx-auto">
          <div className="text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-text-primary mb-4">Real-World Applications</h2>
            <p className="max-w-2xl mx-auto text-text-secondary mb-12">Wherever sensitive records live, Virtual Archives puts them to work.</p>
          </div>
          <div className="use-cases-carousel">
            <div className="use-cases-track">
              {[0, 1].map((copy) => (
                <div key={copy} className="use-cases-group" {...(copy === 0
                  ? { role: "list", "aria-label": "Virtual Archives use cases" } : { "aria-hidden": true })}>
                  {USE_CASES.map((useCase) => (
                    <div key={`${copy}-${useCase.title}`} role={copy === 0 ? "listitem" : undefined}
                         className="w-[300px] sm:w-[340px] md:w-[380px] flex-shrink-0 bg-surface p-6 sm:p-8 rounded-xl border border-borderSubtle shadow-sm hover:shadow-lg hover:shadow-accentPurple/10 transition-all duration-300">
                      <h3 className="text-xl font-bold mb-3 text-primary">{useCase.title}</h3>
                      <p className="text-text-secondary">{useCase.description}</p>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Call to action */}
      <section className="pb-12 sm:pb-16 md:pb-20">
        <div className="relative overflow-hidden bg-primary text-center rounded-2xl p-8 sm:p-10 md:p-12 shadow-xl shadow-primary/25">
          <div className="pointer-events-none absolute inset-0 bg-blueprint opacity-30" aria-hidden="true" />
          <h2 className="relative text-2xl sm:text-3xl md:text-4xl font-bold text-text-inverse mb-4">Your records, your AI, with Contextral</h2>
          <p className="relative max-w-3xl mx-auto text-base sm:text-lg text-surfaceSoft mb-8">
            Add Virtual Archives to your plan and we'll go over it on a short call, or start with a free assessment
            of your document workflows.
          </p>
          <div className="relative flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link to={ADD_TO_PLAN}
                  className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-surface px-6 py-3 text-base font-semibold text-primary shadow-lg transition-all duration-300 hover:scale-105 sm:w-auto sm:px-8 sm:py-4 sm:text-lg">
              Add Virtual Archives to Plan
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" aria-hidden="true" />
            </Link>
            <button type="button" onClick={bookAssessment}
                    className="inline-flex w-full items-center justify-center rounded-full border border-white/40 px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-white/10 sm:w-auto sm:px-8 sm:py-4 sm:text-lg">
              Book a Free Automation Assessment
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VirtualArchives;
